import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CalendarClock } from "lucide-react";
import { SITE_CONFIG } from "../../config/site";
import { track } from "../../lib/analytics";

const CalendlyContext = createContext({ openCalendly: () => {} });
export const useCalendly = () => useContext(CalendlyContext);

export const CalendlyProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openCalendly = () => {
    track("calendly_open");
    setOpen(true);
  };

  return (
    <CalendlyContext.Provider value={{ openCalendly }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-label="Agendar una reunión"
            data-testid="calendly-modal"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="relative w-full max-w-3xl h-[80vh] rounded-2xl overflow-hidden border border-[var(--line)] bg-[var(--surface)]"
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 z-10 p-2 rounded-full bg-[var(--bg2)] border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)]"
                aria-label="Cerrar"
                data-testid="calendly-close-button"
              >
                <X size={18} />
              </button>
              {SITE_CONFIG.calendlyUrl ? (
                <iframe
                  src={`${SITE_CONFIG.calendlyUrl}?hide_gdpr_banner=1&background_color=050b12&text_color=f5f7fa&primary_color=00c7a5`}
                  title="Agenda una reunión con Netsys Panamá"
                  className="w-full h-full border-0"
                  data-testid="calendly-iframe"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center px-8" data-testid="calendly-placeholder">
                  <CalendarClock size={40} className="text-[var(--green)] mb-5" />
                  <h3 className="text-2xl font-bold mb-3">Agenda casi lista</h3>
                  <p className="text-[var(--muted)] max-w-md text-sm leading-relaxed">
                    El enlace de Calendly aún no está configurado. Define{" "}
                    <span className="mono text-[var(--cyan)]">REACT_APP_CALENDLY_URL</span> en la configuración del sitio
                    y este espacio mostrará la agenda en vivo.
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </CalendlyContext.Provider>
  );
};
